import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  Box,
  Typography,
  IconButton,
  CircularProgress,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Shield as ShieldIcon,
  Warning as WarningIcon,
  Lock as LockIcon,
  CheckCircle as CheckCircleIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';

const colors = {
  bg: '#111827',
  cardBg: '#1f2937',
  border: '#374151',
  primary: '#B25373',
  primaryHover: '#8E3D59',
  textWhite: '#ffffff',
  textGray300: '#d1d5db',
  textGray400: '#9ca3af',
  textGray500: '#6b7280',
  danger: '#ef4444',
  dangerLight: '#fca5a5',
  dangerBg: 'rgba(239, 68, 68, 0.1)',
  warningBg: 'rgba(245, 158, 11, 0.1)',
  warningText: '#fcd34d',
  warningIcon: '#f59e0b',
  successBg: 'rgba(34, 197, 94, 0.1)',
  successText: '#86efac',
  successIcon: '#22c55e',
};

const ResetPassword: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  const token = searchParams.get('token');

  const { isLoading, error, resetPassword, clearError } = useAuth();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordStrength, setPasswordStrength] = useState<'weak' | 'medium' | 'strong' | ''>('');
  const [submitted, setSubmitted] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [requirements, setRequirements] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false,
    special: false,
    match: false,
  });

  useEffect(() => {
    if (password) {
      let strength = 0;
      const hasLength = password.length >= 8;
      const hasUppercase = /[A-Z]/.test(password);
      const hasLowercase = /[a-z]/.test(password);
      const hasNumber = /[0-9]/.test(password);
      const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(password);
      const doesMatch = password === confirmPassword && password !== '';

      setRequirements({ length: hasLength, uppercase: hasUppercase, lowercase: hasLowercase, number: hasNumber, special: hasSpecial, match: doesMatch });

      if (hasLength) strength++;
      if (hasUppercase) strength++;
      if (hasLowercase) strength++;
      if (hasNumber) strength++;
      if (hasSpecial) strength++;

      if (strength <= 2) setPasswordStrength('weak');
      else if (strength <= 4) setPasswordStrength('medium');
      else setPasswordStrength('strong');
    } else {
      setPasswordStrength('');
      setRequirements({ length: false, uppercase: false, lowercase: false, number: false, special: false, match: confirmPassword === '' ? false : password === confirmPassword });
    }
  }, [password, confirmPassword]);

  const validatePassword = () => {
    if (password.length < 8) {
      setPasswordError('رمز عبور باید حداقل 8 کاراکتر باشد');
      return false;
    }
    if (password !== confirmPassword) {
      setPasswordError('رمز عبور و تکرار آن مطابقت ندارند');
      return false;
    }
    setPasswordError('');
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    if (!validatePassword()) return;
    if (!token) { setPasswordError('توکن نامعتبر است'); return; }

    try {
      await resetPassword(token, password, confirmPassword);
      setSubmitted(true);
      setTimeout(() => navigate('/login'), 3000);
    } catch (err) {
      // Error handled by context
    }
  };

  const inputStyle: React.CSSProperties = {
    display: 'block',
    width: '100%',
    paddingRight: 40,
    paddingLeft: 40,
    paddingTop: 10,
    paddingBottom: 10,
    border: `1px solid ${colors.border}`,
    borderRadius: 8,
    backgroundColor: colors.cardBg,
    color: colors.textWhite,
    fontSize: 14,
    fontFamily: 'inherit',
    direction: 'ltr',
    textAlign: 'left',
    outline: 'none',
    boxSizing: 'border-box',
  };

  const strengthColor = passwordStrength === 'weak' ? colors.danger : passwordStrength === 'medium' ? colors.warningIcon : passwordStrength === 'strong' ? colors.successIcon : colors.border;
  const strengthWidth = passwordStrength === 'weak' ? '30%' : passwordStrength === 'medium' ? '70%' : passwordStrength === 'strong' ? '100%' : '0%';
  const strengthLabel = passwordStrength === 'weak' ? 'ضعیف' : passwordStrength === 'medium' ? 'متوسط' : passwordStrength === 'strong' ? 'قوی' : '';

  const requirementItems = [
    { key: 'length', label: 'حداقل ۸ کاراکتر', satisfied: requirements.length },
    { key: 'uppercase', label: 'حروف بزرگ (A-Z)', satisfied: requirements.uppercase },
    { key: 'lowercase', label: 'حروف کوچک (a-z)', satisfied: requirements.lowercase },
    { key: 'number', label: 'حداقل یک عدد (0-9)', satisfied: requirements.number },
    { key: 'special', label: 'کاراکتر خاص (!@#$)', satisfied: requirements.special },
  ];

  return (
    <Box
      dir="rtl"
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: colors.bg,
        padding: '48px 16px',
      }}
    >
      <Box sx={{ maxWidth: 448, width: '100%', display: 'flex', flexDirection: 'column', gap: 4 }}>
        {/* Header */}
        <Box sx={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Box sx={{ width: 64, height: 64, borderRadius: '50%', backgroundColor: colors.primary, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 3 }}>
            <ShieldIcon sx={{ fontSize: 40, color: '#fff' }} />
          </Box>
          <Typography sx={{ fontSize: 30, fontWeight: 800, color: colors.textWhite, lineHeight: 1.3 }}>
            بازنشانی رمز عبور
          </Typography>
          <Typography sx={{ mt: 1, fontSize: 14, color: colors.textGray400 }}>
            {submitted ? 'رمز عبور با موفقیت تغییر کرد' : 'رمز عبور جدیدی برای حساب خود تعیین کنید'}
          </Typography>
        </Box>

        {!submitted ? (
          <>
            {/* Errors */}
            {error && (
              <Box sx={{ borderRadius: 2, p: 2, display: 'flex', alignItems: 'flex-start', gap: 1.5, backgroundColor: colors.dangerBg }}>
                <WarningIcon sx={{ color: colors.danger, fontSize: 20, mt: 0.25, flexShrink: 0 }} />
                <Typography sx={{ fontSize: 14, color: colors.dangerLight, lineHeight: 1.6 }}>{error}</Typography>
              </Box>
            )}
            {passwordError && (
              <Box sx={{ borderRadius: 2, p: 2, display: 'flex', alignItems: 'flex-start', gap: 1.5, backgroundColor: colors.warningBg }}>
                <WarningIcon sx={{ color: colors.warningIcon, fontSize: 20, mt: 0.25, flexShrink: 0 }} />
                <Typography sx={{ fontSize: 14, color: colors.warningText, lineHeight: 1.6 }}>{passwordError}</Typography>
              </Box>
            )}

            {/* Form */}
            <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Password */}
              <Box>
                <Typography component="label" htmlFor="password" sx={{ display: 'block', fontSize: 14, fontWeight: 500, color: colors.textGray300, mb: 0.5 }}>
                  رمز عبور جدید
                </Typography>
                <Box sx={{ position: 'relative', mt: 0.5 }}>
                  <Box sx={{ position: 'absolute', top: 0, bottom: 0, right: 0, pr: 1.5, display: 'flex', alignItems: 'center', pointerEvents: 'none', zIndex: 1 }}>
                    <LockIcon sx={{ fontSize: 20, color: colors.textGray500 }} />
                  </Box>
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••••••"
                    style={inputStyle}
                    onFocus={(e) => { e.target.style.borderColor = colors.primary; e.target.style.boxShadow = '0 0 0 2px rgba(178, 83, 115, 0.3)'; }}
                    onBlur={(e) => { e.target.style.borderColor = colors.border; e.target.style.boxShadow = 'none'; }}
                  />
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    tabIndex={-1}
                    disableRipple
                    sx={{ position: 'absolute', top: '50%', left: 4, transform: 'translateY(-50%)', color: colors.textGray500, '&:hover': { color: colors.textGray400, backgroundColor: 'transparent' } }}
                  >
                    {showPassword ? <VisibilityOff sx={{ fontSize: 20 }} /> : <Visibility sx={{ fontSize: 20 }} />}
                  </IconButton>
                </Box>
              </Box>

              {/* Password Strength */}
              {password && (
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                    <Typography sx={{ fontSize: 13, color: colors.textGray400 }}>قدرت رمز عبور:</Typography>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: strengthColor }}>{strengthLabel}</Typography>
                  </Box>
                  <Box sx={{ height: 4, borderRadius: 2, backgroundColor: '#374151', position: 'relative', overflow: 'hidden', mb: 2 }}>
                    <Box sx={{ position: 'absolute', top: 0, right: 0, height: '100%', borderRadius: 2, width: strengthWidth, backgroundColor: strengthColor, transition: 'all 0.3s ease' }} />
                  </Box>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 0.5 }}>
                    {requirementItems.map((req) => (
                      <Box key={req.key} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.25 }}>
                        <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: req.satisfied ? colors.successIcon : '#4b5563', flexShrink: 0 }} />
                        <Typography sx={{ fontSize: 12, color: req.satisfied ? colors.successIcon : colors.textGray500 }}>{req.label}</Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}

              {/* Confirm Password */}
              <Box>
                <Typography component="label" htmlFor="confirm-password" sx={{ display: 'block', fontSize: 14, fontWeight: 500, color: colors.textGray300, mb: 0.5 }}>
                  تکرار رمز عبور
                </Typography>
                <Box sx={{ position: 'relative', mt: 0.5 }}>
                  <Box sx={{ position: 'absolute', top: 0, bottom: 0, right: 0, pr: 1.5, display: 'flex', alignItems: 'center', pointerEvents: 'none', zIndex: 1 }}>
                    <LockIcon sx={{ fontSize: 20, color: colors.textGray500 }} />
                  </Box>
                  <input
                    id="confirm-password"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="••••••••••••"
                    style={inputStyle}
                    onFocus={(e) => { e.target.style.borderColor = colors.primary; e.target.style.boxShadow = '0 0 0 2px rgba(178, 83, 115, 0.3)'; }}
                    onBlur={(e) => { e.target.style.borderColor = colors.border; e.target.style.boxShadow = 'none'; }}
                  />
                  <IconButton
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    tabIndex={-1}
                    disableRipple
                    sx={{ position: 'absolute', top: '50%', left: 4, transform: 'translateY(-50%)', color: colors.textGray500, '&:hover': { color: colors.textGray400, backgroundColor: 'transparent' } }}
                  >
                    {showConfirmPassword ? <VisibilityOff sx={{ fontSize: 20 }} /> : <Visibility sx={{ fontSize: 20 }} />}
                  </IconButton>
                </Box>
              </Box>

              {/* Match indicator */}
              {password && confirmPassword && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: requirements.match ? colors.successIcon : colors.danger }} />
                  <Typography sx={{ fontSize: 12, color: requirements.match ? colors.successIcon : colors.danger }}>
                    {requirements.match ? 'رمز عبور و تکرار آن مطابقت دارند' : 'رمز عبور و تکرار آن مطابقت ندارند'}
                  </Typography>
                </Box>
              )}

              {/* Submit */}
              <button
                type="submit"
                disabled={isLoading || !passwordStrength || !requirements.match || !token}
                style={{
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: 8,
                  padding: '10px 16px',
                  border: 'none',
                  borderRadius: 8,
                  boxShadow: '0 1px 2px rgba(0,0,0,0.3)',
                  fontSize: 14,
                  fontWeight: 500,
                  fontFamily: 'inherit',
                  color: colors.textWhite,
                  backgroundColor: colors.primary,
                  cursor: isLoading || !passwordStrength || !requirements.match || !token ? 'not-allowed' : 'pointer',
                  opacity: isLoading || !passwordStrength || !requirements.match || !token ? 0.5 : 1,
                }}
                onMouseEnter={(e) => { if (!isLoading) (e.target as HTMLElement).style.backgroundColor = colors.primaryHover; }}
                onMouseLeave={(e) => { if (!isLoading) (e.target as HTMLElement).style.backgroundColor = colors.primary; }}
              >
                {isLoading ? (
                  <>
                    <CircularProgress size={20} sx={{ color: '#fff' }} />
                    <span>در حال ذخیره...</span>
                  </>
                ) : (
                  'بازنشانی رمز عبور'
                )}
              </button>

              {/* Back */}
              <button
                type="button"
                onClick={() => navigate('/login')}
                style={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 6, padding: '8px 16px', border: 'none', borderRadius: 8, fontSize: 14, fontFamily: 'inherit', color: colors.textGray400, backgroundColor: 'transparent', cursor: 'pointer' }}
              >
                <ArrowBackIcon sx={{ fontSize: 16 }} />
                بازگشت به صفحه ورود
              </button>
            </Box>
          </>
        ) : (
          <>
            {/* Success */}
            <Box sx={{ borderRadius: 2, p: 3, backgroundColor: colors.successBg, textAlign: 'center' }}>
              <Box sx={{ width: 56, height: 56, borderRadius: '50%', backgroundColor: 'rgba(34, 197, 94, 0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                <CheckCircleIcon sx={{ fontSize: 32, color: colors.successIcon }} />
              </Box>
              <Typography sx={{ fontSize: 18, fontWeight: 600, color: colors.successIcon, mb: 1 }}>
                رمز عبور با موفقیت تغییر کرد
              </Typography>
              <Typography sx={{ fontSize: 14, color: colors.successText, lineHeight: 1.8 }}>
                رمز عبور شما با موفقیت به‌روزرسانی شد. در حال انتقال به صفحه ورود...
              </Typography>
              {/* Progress bar */}
              <Box sx={{ mt: 2, height: 4, borderRadius: 2, backgroundColor: '#374151', overflow: 'hidden', position: 'relative' }}>
                <Box
                  sx={{
                    position: 'absolute', top: 0, right: 0, height: '100%', borderRadius: 2,
                    backgroundColor: colors.successIcon,
                    animation: 'progress 3s linear',
                    '@keyframes progress': { '0%': { width: '0%' }, '100%': { width: '100%' } },
                  }}
                />
              </Box>
            </Box>
          </>
        )}

        {/* Security Notice */}
        <Box sx={{ textAlign: 'center' }}>
          <Typography sx={{ fontSize: 12, color: colors.textGray500, lineHeight: 1.8 }}>این بخش امن مدیریت است.</Typography>
          <Typography sx={{ fontSize: 12, color: colors.textGray500, lineHeight: 1.8 }}>تمام فعالیت‌ها ثبت و نظارت می‌شوند.</Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default ResetPassword;
